import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import constant from '../../constant';
import DeviceForm from '../ui/deviceForm';

const EditDevice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/device/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (response.ok) {
          const data = result;
          setInitialValues({
            device_id: data.device_id,
            device_name: data.device_name,
            device_mac: data.device_mac,
            is_active: data.is_active,
            created_by: data.created_by || 'system',
            created_on: data.created_on,
          });
        } else {
          setError(result.message || 'Failed to fetch device data');
        }
      } catch (error) {
        setError('Error fetching device data');
      } finally {
        setLoading(false);
      }
    };

    fetchDevice();
  }, [id]);

  const handleCancel = () => {
    navigate('/admin/devices');
  };

  const handleSubmit = async (deviceData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const apiData = {
        device_name: deviceData.device_name,
        mac_address: deviceData.device_mac,
        is_active: deviceData.is_active,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/device/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();
      if (response.ok) {
        navigate('/admin/devices');
      } else {
        setError(result.message || 'Failed to update device');
      }
    } catch (error) {
      setError('Error updating device');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6 text-red-600">
        {error}
        <button
          onClick={() => setError(null)}
          className="ml-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Clear Error
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Edit Device</h2>
          </div>
        </div>
        {loading && (
          <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-6">
            Processing...
          </div>
        )}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Clear Error
            </button>
          </div>
        )}
        <DeviceForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isEditMode={true}
        />
      </div>
    </div>
  );
};

export default EditDevice;